import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Package, Award, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { agentApi } from "@/lib/extensions-api";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { EmptyState } from "@/components/ui/EmptyState";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";

export function AgentVendorOrdersPage() {
  const qc = useQueryClient();
  const [givePointOrder, setGivePointOrder] = useState<any>(null);
  const [points, setPoints] = useState("");

  const orders = useQuery({
    queryKey: ["agent-vendor-orders"],
    queryFn: agentApi.vendorOrders,
  });

  // Automatically suggest points based on order total when modal opens
  const suggestion = useQuery({
    queryKey: ["agent-suggest-points", givePointOrder?.total],
    queryFn: () => agentApi.suggestPoints(Number(givePointOrder.total)),
    enabled: !!givePointOrder,
  });

  const distribute = useMutation({
    mutationFn: () => agentApi.distribute({
      user_id: givePointOrder.customer_id,
      points: Number(points),
      reference: `Order ${givePointOrder.order_id}`,
    }),
    onSuccess: () => {
      toast.success("Points successfully awarded to customer!");
      agentApi.updateOrderStatus(givePointOrder.order_id, "completed").then(() => {
        qc.invalidateQueries({ queryKey: ["agent-vendor-orders"] });
        qc.invalidateQueries({ queryKey: ["agent-profile"] });
      });
      setGivePointOrder(null);
    },
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Vendor Orders</h1>
        <p className="text-sm text-surface-500 mt-1">Manage orders placed at your shop. Once completed, issue points to the customer.</p>
      </header>

      <Card>
        <CardBody className="p-0">
          {(orders.data ?? []).length === 0 ? (
             <EmptyState icon={Package} title="No shop orders yet" />
          ) : (
            <Table>
              <THead>
                <TR><TH>Order ID</TH><TH>Customer</TH><TH>Amount</TH><TH>Status</TH><TH>Action</TH></TR>
              </THead>
              <tbody>
                {orders.data!.map(o => (
                  <TR key={o.order_id}>
                    <TD className="font-mono font-bold">{o.order_id}</TD>
                    <TD>{o.customer_id}</TD>
                    <TD className="font-bold text-brand-700">{formatCurrency(o.total)}</TD>
                    <TD><StatusPill status={o.status} /></TD>
                    <TD>
                      {o.status === "payment_verification_pending" && (
                        <span className="text-xs text-warning-600">Awaiting Admin Approval</span>
                      )}
                      {o.status === "completed" && (
                        <span className="text-xs text-success-600 font-bold flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> Done</span>
                      )}
                      {o.status === "processing" && (
                        <Button size="sm" onClick={() => { setGivePointOrder(o); setPoints(suggestion.data?.suggested_points?.toString() || ""); }}>
                          Deliver & Give Points
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {givePointOrder && (
        <Modal open onClose={() => setGivePointOrder(null)} title="Complete Order & Give Points">
          <div className="space-y-4">
            <div className="bg-brand-50 p-4 rounded-lg border border-brand-200 text-sm">
              <div className="font-bold text-brand-900 mb-1">Order {givePointOrder.order_id}</div>
              <div>Customer: {givePointOrder.customer_id}</div>
              <div>Total Amount: {formatCurrency(givePointOrder.total)}</div>
            </div>
            
            <div className="text-xs font-semibold text-warning-700 uppercase">System Suggestion: {suggestion.data?.suggested_points || 0} Points</div>
            
            <Input 
              label="Points to award" 
              type="number" 
              value={points} 
              onChange={(e) => setPoints(e.target.value)} 
            />
            
            <Button fullWidth size="lg" leftIcon={<Award className="w-4 h-4"/>} loading={distribute.isPending} onClick={() => distribute.mutate()}>
              Confirm Delivery & Deduct Points
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}